// 搬入ページ (mock UX 準拠 / 実API)。CarryOut と同型の api-direct (実API nested GET → useState)。
//   /carry-in/                  一覧 (工事選択 + 搬入一覧/搬入日 の2タブ・各搬入をAccordion)
//   /carry-in/new/              新規 (8項目フォーム・搬入場所は工事のエリアで絞込)
//   /carry-in/:id/              詳細 (InfoList + 搬入時QR読取)
//   /carry-in/:id/edit/         編集
//   /carry-in/:id/scan/         QRスキャン (/process-carry-in)
// 当面オンライン限定。データは実API直 (api.carry_in)。工事は Work マスタに一本化済み。

import { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { PageContext } from "../PageContext";
import { WorkBase, WorkBaseSchema } from "../../../schema/generated/Work";
import { CarryInLocationBaseSchema } from "../../../schema/generated/CarryInLocation";
import { useMaterialMasters } from "../../../util/useMaterialMasters";
import { useIsOnline } from "../../../util/useIsOnline";
import { getRowsChecked } from "../../../util/getRowsChecked";
import { LocationRow, LocationsById } from "./helpers";
import { CarryInListPage } from "./CarryInListPage";
import { CarryInDetailPage } from "./CarryInDetailPage";
import { CarryInFormPage } from "./CarryInFormPage";
import { CarryInScanPage } from "./CarryInScanPage";
import { errorMessageOf } from "../../../util/typed";

export default function CarryInPage({ pageContext }: { pageContext: PageContext }) {
  const isOnline = useIsOnline(pageContext.api);
  const [locations, setLocations] = useState<LocationRow[] | null>(null);
  const { materialsById } = useMaterialMasters(pageContext.api);
  const [works, setWorks] = useState<WorkBase[]>([]);

  useEffect(() => {
    // オフライン時は取得しない(getRowsFromServer は IDB フォールバックが無くエラーダイアログが連発するため)。復帰時に取得する。
    if (!isOnline) {
      return;
    }
    let active = true;
    getRowsChecked(pageContext.api, "carry-in-locations", CarryInLocationBaseSchema.base)
      .then((rows) => { if (active) { setLocations(rows); } })
      .catch((e) => {
        console.warn("carry-in-locations fetch failed", e);
        if (active) {
          setLocations([]);
          pageContext.api.showError(`搬入場所マスタの取得に失敗しました。\n${errorMessageOf(e)}`);
        }
      });
    getRowsChecked(pageContext.api, "works", WorkBaseSchema.base)
      .then((rows) => { if (active) { setWorks(rows); } })
      .catch((e) => {
        console.warn("works fetch failed", e);
        if (active) {
          pageContext.api.showError(`工事マスタの取得に失敗しました。\n${errorMessageOf(e)}`);
        }
      });
    return () => { active = false; };
  }, [pageContext, isOnline]);

  const workList = useMemo(() => works.filter((w) => w.deleted_at === null), [works]);
  const locationList = useMemo(() => locations ?? [], [locations]);
  const locationsById = useMemo(() => {
    const map: LocationsById = new Map();
    (locations ?? []).forEach((l) => map.set(l.id, l));
    return map;
  }, [locations]);

  return (
    <Routes>
      <Route index element={<CarryInListPage pageContext={pageContext} workList={workList} materialsById={materialsById} />} />
      <Route path="new/" element={<CarryInFormPage pageContext={pageContext} workList={workList} locationList={locationList} mode="new" />} />
      <Route path=":id/" element={<CarryInDetailPage pageContext={pageContext} locationsById={locationsById} materialsById={materialsById} />} />
      <Route path=":id/edit/" element={<CarryInFormPage pageContext={pageContext} workList={workList} locationList={locationList} mode="edit" />} />
      <Route path=":id/scan/" element={<CarryInScanPage pageContext={pageContext} />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
